import { writable } from 'svelte/store';
import {
	defaultSelectedRoom,
	defaultThermostatFilter,
	type SelectOptionPropsTypeExtended,
} from 'src/helpers/constants';
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
import { deepCopy } from 'src/helpers/utilities';

export interface FilterOption {
	title: string;
	translationDefault: string;
	selected: boolean;
	id: string | number;
}

export interface DevicesFilters {
	rooms: SelectOptionPropsTypeExtended[];
	device_state: FilterOption[];
	shared_devices: FilterOption[];
	appliance_types: FilterOption[];
	cloud_connectivity: FilterOption[];
	more_filters: FilterOption[];
	firmware_version: string;
}

export interface ScenesFilters {
	rooms: SelectOptionPropsTypeExtended[];
}

export interface GroupsFilters {
	rooms: SelectOptionPropsTypeExtended[];
}

export interface ActivityLogFilters {
	rooms: SelectOptionPropsTypeExtended[];
}

export interface ThermostatsFilters {
	rooms: SelectOptionPropsTypeExtended[];
	type: SelectOptionPropsTypeExtended[];
}

export const devicesFiltersInitialState = {
	rooms: [defaultSelectedRoom],
	device_state: [
		{
			id: 'online',
			title: 'online',
			translationDefault: 'Online',
			selected: false,
		},
		{
			id: 'offline',
			title: 'offline',
			translationDefault: 'Offline',
			selected: false,
		},
	],
	shared_devices: [
		{
			id: 'shared',
			title: 'shared devices',
			value: -20,
			translationDefault: 'shared devices',
			selected: false,
		},
		{
			id: 'all_shared',
			title: 'all shared devices',
			value: -40,
			translationDefault: 'all shared devices',
			selected: false,
		},
		{
			id: 'all_owned',
			title: 'all owned devices',
			value: -40,
			translationDefault: 'all owned devices',
			selected: false,
		},
		{
			id: 'shared_by_me',
			title: 'shared devices by me',
			value: -52,
			translationDefault: 'shared devices by me',
			selected: false,
		},
	],
	cloud_connectivity: [
		{
			id: 'cloudEnabled',
			title: 'cloud enabled',
			translationDefault: 'Cloud enabled',
			selected: false,
		},
		{
			id: 'cloudDisabled',
			title: 'cloud disabled',
			translationDefault: 'Cloud disabled',
			selected: false,
		},
	],
	more_filters: [
		{
			id: 'rangeExtender',
			title: 'range extenders',
			translationDefault: 'Range extenders',
			selected: false,
		},
		{
			id: 'activeRangeExtender',
			title: 'active range extenders',
			translationDefault: 'Active range extenders',
			selected: false,
		},
		{
			id: 'bluetoothDevices',
			title: 'bluetooth devices',
			translationDefault: 'Bluetooth devices',
			selected: false,
		},
		{
			id: 'bluetoothGateways',
			title: 'bluetooth gateways',
			translationDefault: 'Bluetooth gateways',
			selected: false,
		},
		{
			id: 'activeBluetoothGateways',
			title: 'active bluetooth gateways',
			translationDefault: 'Active bluetooth gateways',
			selected: false,
		},
		{
			id: 'activatedAIModel',
			title: 'active AI models',
			translationDefault: 'Active AI models',
			selected: false,
		},
		{
			id: 'virtualDevices',
			title: 'virtual devices',
			translationDefault: 'Virtual Devices',
			selected: false,
		},
		{
			id: 'peripheralDevices',
			title: 'peripheral devices',
			translationDefault: 'Peripheral Devices',
			selected: false,
		},
		{
			id: 'activeAccessPoint',
			title: 'active access point',
			translationDefault: 'Active access point',
			selected: false,
		},
	],
	firmware_version: '',
	appliance_types: [
		{
			id: 2,
			title: 'lighting',
			translationDefault: 'Lighting',
			selected: false,
		},
		{
			id: 3,
			title: 'entertainment',
			translationDefault: 'Entertainment',
			selected: false,
		},
		{
			id: 4,
			title: 'heating and cooling',
			translationDefault: 'heating cooling',
			selected: false,
		},
		{
			id: 5,
			title: 'refrigeration',
			translationDefault: 'Refrigeration',
			selected: false,
		},
		{
			id: 6,
			title: 'laundry and cleaning',
			translationDefault: 'Laundry cleaning',
			selected: false,
		},
		{
			id: 7,
			title: 'cooking',
			translationDefault: 'Cooking',
			selected: false,
		},
		{
			id: 8,
			title: 'pool and garden',
			translationDefault: 'Pool and garden',
			selected: false,
		},
		{
			id: 9,
			title: 'electric vehicle',
			translationDefault: 'Electric vehicle',
			selected: false,
		},
		{
			id: 10,
			title: 'ventilation',
			translationDefault: 'Ventilation',
			selected: false,
		},
		{
			id: 11,
			title: 'grid source',
			translationDefault: 'Grid source',
			selected: false,
		},
		{
			id: 12,
			title: 'generated energy',
			translationDefault: 'Generated energy',
			selected: false,
		},
		{
			id: 13,
			title: 'water heater',
			translationDefault: 'Water heater',
			selected: false,
		},
		{
			id: 14,
			title: 'other',
			translationDefault: 'Other',
			selected: false,
		},
		{
			id: 15,
			title: 'roller',
			translationDefault: 'Roller',
			selected: false,
		},
		{
			id: 16,
			title: 'garage_door',
			translationDefault: 'Garage door',
			selected: false,
		},
		{
			id: 17,
			title: 'game console',
			translationDefault: 'Game console',
			selected: false,
		},
		{
			id: 18,
			title: 'computer',
			translationDefault: 'Computer',
			selected: false,
		},
		{
			id: 19,
			title: 'speaker system',
			translationDefault: 'Speaker system',
			selected: false,
		},
		{
			id: 20,
			title: 'projector',
			translationDefault: 'Projector',
			selected: false,
		},
	],
};
export const devicesFilters = writable<DevicesFilters>({
	...devicesFiltersInitialState,
});

export const roomsScenesFilterInitialState = {
	rooms: [defaultSelectedRoom],
};

export const roomsScenesFilter = writable<ScenesFilters>(roomsScenesFilterInitialState);

export const roomsGroupsFilterInitialState = {
	rooms: [defaultSelectedRoom],
};

export const thermostatsFilterInitialState = {
	rooms: [defaultSelectedRoom],
	type: [defaultThermostatFilter],
};

export const thermostatsFilter = writable<ThermostatsFilters>(thermostatsFilterInitialState);

export const roomsGroupsFilter = writable<GroupsFilters>(roomsGroupsFilterInitialState);

export const roomsActivityLogFilterInitialState = {
	rooms: [defaultSelectedRoom],
};

export const roomsActivityLogFilter = writable<ActivityLogFilters>(roomsActivityLogFilterInitialState);

export const deviceMainFilter = writable('');

export const resetDeviceMainFilter = () => {
	deviceMainFilter.set('');
};

export const groupMainFilter = writable('');

export const resetGroupMainFilter = () => {
	groupMainFilter.set('');
};

export const thermostatsMainFilter = writable('');

export const resetThermostatsMainFilter = () => {
	thermostatsMainFilter.set('');
};

export const sceneMainFilter = writable('');

export const resetSceneMainFilter = () => {
	sceneMainFilter.set('');
};

export const alarmMainFilter = writable('');

export const resetAlarmMainFilter = () => {
	alarmMainFilter.set('');
};

export const roomMainFilter = writable('');

export const resetRoomMainFilter = () => {
	roomMainFilter.set('');
};

export const resetFiltersAllDevicesList = () => {
	deviceMainFilter.set('');
	devicesFilters.set(deepCopy(devicesFiltersInitialState));
};

/**
 *
 * @param store - devices filters store
 * @returns - how many devices filters are currently selected
 */
export const getSelectedDeviceFilterCount = (store) => {
	let count = 0;
	for (const filter in store) {
		if (typeof store[filter] === 'string' && store[filter] !== '') {
			count++;
		} else if (Array.isArray(store[filter])) {
			store[filter].forEach((item) => {
				if (item.hasOwnProperty('selected') && item.selected === true) {
					count++;
				} else if (item.value !== 'all' && !item.hasOwnProperty('selected')) {
					count++;
				} else if (item.value === 'all' && !item.hasOwnProperty('selected')) {
					count = 0;
				}
			});
		}
	}

	return count;
};

/**
 *
 * @param store - groups filters store
 * @returns - number of active filters (1 if room filter is not 'all', 0 otherwise)
 */
export const getSelectedGroupFilterCount = (store: GroupsFilters): number => {
	const selectedRoom = store.rooms.find((room) => room.value);
	return selectedRoom && selectedRoom.value !== defaultSelectedRoom.value ? 1 : 0;
};

/**
 *
 * @param store - scenes filters store
 * @returns - number of active filters (1 if room filter is not 'all', 0 otherwise)
 */
export const getSelectedSceneFilterCount = (store: ScenesFilters): number => {
	const selectedRoom = store.rooms.find((room) => room.value);
	return selectedRoom && selectedRoom.value !== defaultSelectedRoom.value ? 1 : 0;
};

/**
 *
 * @param store - thermostats filters store
 * @returns - number of active filters (count of rooms and type filters that are not 'all')
 */
export const getSelectedThermostatFilterCount = (store: ThermostatsFilters): number => {
	let count = 0;

	const selectedRoom = store.rooms.find((room) => room.value);
	if (selectedRoom && selectedRoom.value !== defaultSelectedRoom.value) {
		count++;
	}

	const selectedType = store.type.find((type) => type.value);
	if (selectedType && selectedType.value !== defaultThermostatFilter.value) {
		count++;
	}

	return count;
};
